import {
  CmpInvoiceActionTypeEnum,
  CmpInvoiceDisplayStateEnum,
  CmpInvoiceKindEnum,
} from "../../../../graphql/types";

export type InvoiceMenuStatus =
  | "draft"
  | "outstanding"
  | "partlyPaid"
  | "paid"
  | "refunded"
  | "voided"
  | "waived"
  | "complimentary"
  | "other";

export type InvoiceRowActionKey =
  | "editInvoice"
  | "approveInvoice"
  | "printPdf"
  | "applyPayment"
  | "emailInvoice"
  | "creditRefund"
  | "voidInvoice"
  | "deleteDraft";

export const isOutstandingInvoiceDisplayState = (
  displayState: CmpInvoiceDisplayStateEnum | string,
) =>
  displayState === CmpInvoiceDisplayStateEnum.NotPaid ||
  displayState === CmpInvoiceDisplayStateEnum.AwaitingPayment ||
  displayState === CmpInvoiceDisplayStateEnum.Overdue;

export const isCreditedInvoiceDisplayState = (
  displayState: CmpInvoiceDisplayStateEnum | string,
) =>
  displayState === CmpInvoiceDisplayStateEnum.Refunded ||
  displayState === CmpInvoiceDisplayStateEnum.Credited;

type ManageInvoiceActionAvailabilityRow = {
  availableActions: ReadonlyArray<{
    action: CmpInvoiceActionTypeEnum;
    available: boolean;
  }>;
  displayState: CmpInvoiceDisplayStateEnum;
  invoiceKind: CmpInvoiceKindEnum;
  refundableAmountInCents: number;
  totalPaidInCents: number;
};

export const isEventInvoiceKind = (
  invoiceKind: CmpInvoiceKindEnum,
): boolean =>
  invoiceKind === CmpInvoiceKindEnum.EventRegistration ||
  invoiceKind === CmpInvoiceKindEnum.EventSponsorship;

const canUseInvoiceAction = (
  row: ManageInvoiceActionAvailabilityRow,
  action: CmpInvoiceActionTypeEnum,
) =>
  !!row.availableActions.find(
    (availableAction) => availableAction.action === action,
  )?.available;

// CAP offers a payment request on an event invoice only when it has no event
// sale to keep in step (an imported invoice); it then emails like any invoice.
export const emailsManageInvoicePaymentRequest = (
  row: ManageInvoiceActionAvailabilityRow,
): boolean =>
  isOutstandingInvoiceDisplayState(row.displayState) &&
  (!isEventInvoiceKind(row.invoiceKind) ||
    canUseInvoiceAction(row, CmpInvoiceActionTypeEnum.SendPaymentRequest) ||
    canUseInvoiceAction(row, CmpInvoiceActionTypeEnum.ResendPaymentRequest));

export const canEmailManageInvoice = (
  row: ManageInvoiceActionAvailabilityRow,
): boolean =>
  emailsManageInvoicePaymentRequest(row)
    ? canUseInvoiceAction(row, CmpInvoiceActionTypeEnum.SendPaymentRequest) ||
      canUseInvoiceAction(row, CmpInvoiceActionTypeEnum.ResendPaymentRequest)
    : canUseInvoiceAction(row, CmpInvoiceActionTypeEnum.EmailDocument);

export const canCreditOrRefundManageInvoice = (
  row: ManageInvoiceActionAvailabilityRow,
): boolean =>
  ((row.displayState === CmpInvoiceDisplayStateEnum.Paid &&
    row.refundableAmountInCents > 0) ||
    (isOutstandingInvoiceDisplayState(row.displayState) &&
      row.refundableAmountInCents > 0)) &&
  (isEventInvoiceKind(row.invoiceKind) ||
    canUseInvoiceAction(row, CmpInvoiceActionTypeEnum.IssueCredit));

export const canVoidManageInvoice = (
  row: ManageInvoiceActionAvailabilityRow,
): boolean =>
  (isOutstandingInvoiceDisplayState(row.displayState) ||
    // A waived ticket sale, or a complimentary ($0) ticket invoice that CAP
    // marks voidable (IF11 EV3, design (21) MENUS.Complimentary).
    ((row.displayState === CmpInvoiceDisplayStateEnum.Waived ||
      row.displayState === CmpInvoiceDisplayStateEnum.Paid) &&
      row.invoiceKind === CmpInvoiceKindEnum.EventRegistration)) &&
  row.totalPaidInCents === 0 &&
  (row.invoiceKind === CmpInvoiceKindEnum.EventSponsorship ||
    canUseInvoiceAction(row, CmpInvoiceActionTypeEnum.Void));

export const invoiceRowActionMatrix: Readonly<
  Record<InvoiceMenuStatus, ReadonlyArray<InvoiceRowActionKey>>
> = {
  // Claude Design InvoiceActionsMenu: Draft = Edit, View/Approve, Delete.
  draft: ["editInvoice", "approveInvoice", "deleteDraft"],
  outstanding: ["applyPayment", "printPdf", "emailInvoice", "voidInvoice"],
  // Production-only states the design has no menu for. A partly paid invoice
  // cannot be voided, so Credit/Refund replaces Void. A waived event
  // registration keeps Void so staff can still release its tickets.
  partlyPaid: ["applyPayment", "printPdf", "emailInvoice", "creditRefund"],
  paid: ["printPdf", "emailInvoice", "creditRefund"],
  refunded: ["printPdf", "emailInvoice"],
  voided: ["printPdf", "emailInvoice"],
  waived: ["printPdf", "emailInvoice", "voidInvoice"],
  // Design (21) InvoiceActionsMenu.jsx:96 MENUS.Complimentary.
  complimentary: ["printPdf", "emailInvoice", "voidInvoice"],
  other: ["printPdf"],
};

export const getInvoiceMenuStatus = ({
  displayState,
  totalPaidInCents,
  invoiceKind,
  availableActions,
}: {
  displayState: CmpInvoiceDisplayStateEnum;
  totalPaidInCents: number;
  invoiceKind?: CmpInvoiceKindEnum;
  availableActions?: ManageInvoiceActionAvailabilityRow["availableActions"];
}): InvoiceMenuStatus => {
  if (isOutstandingInvoiceDisplayState(displayState)) {
    return totalPaidInCents > 0 ? "partlyPaid" : "outstanding";
  }

  if (isCreditedInvoiceDisplayState(displayState)) {
    return "refunded";
  }

  if (displayState === CmpInvoiceDisplayStateEnum.Waived &&
    invoiceKind === CmpInvoiceKindEnum.EventRegistration && totalPaidInCents === 0 &&
    availableActions?.some(action => action.action === CmpInvoiceActionTypeEnum.Void && action.available)) {
    return "waived";
  }
  // A Paid $0 ticket invoice that CAP lets staff void is a complimentary ticket.
  if (displayState === CmpInvoiceDisplayStateEnum.Paid &&
    invoiceKind === CmpInvoiceKindEnum.EventRegistration && totalPaidInCents === 0 &&
    availableActions?.some(action => action.action === CmpInvoiceActionTypeEnum.Void && action.available)) {
    return "complimentary";
  }

  switch (displayState) {
    case CmpInvoiceDisplayStateEnum.Draft:
      return "draft";
    case CmpInvoiceDisplayStateEnum.Paid:
      return "paid";
    case CmpInvoiceDisplayStateEnum.Voided:
      return "voided";
    default:
      return "other";
  }
};
